// Briefblip API client.
//
// Backend served at api.briefblip.com (Caddy on colin to ai-portal on chloe).
// Separate origin from briefblip.com, so we call absolute URL + CORS; no cookies.
// Override via VITE_API_BASE for local dev.

import { useMutation } from '@tanstack/react-query';

export const API_BASE =
  import.meta.env.VITE_API_BASE ?? 'https://api.briefblip.com';

export interface ActionItem {
  text: string;
  owner: string | null;
}

export interface Attendee {
  name: string;
  role: string | null;
  internal?: boolean;
  title?: string | null;
  department?: string | null;
}

export interface CostInfo {
  usd: number;
  input_tokens: number;
  output_tokens: number;
}

export interface ExtractResult {
  title: string;
  attendees?: Attendee[];
  meeting_date?: string | null;
  meeting_time?: string | null;
  location?: string | null;
  decisions: string[];
  action_items: ActionItem[];
  next_steps: string[];
  recap: string;
  model?: string;
  cost?: CostInfo;
}

export class BriefblipError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function extractMeeting(input: {
  transcript: string;
  title?: string;
}): Promise<ExtractResult> {
  const res = await fetch(`${API_BASE}/api/briefblip/extract.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });

  const ct = res.headers.get('content-type') ?? '';
  const payload = ct.includes('application/json')
    ? await res.json()
    : await res.text();

  if (!res.ok) {
    const message =
      typeof payload === 'object' && payload && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (HTTP ${res.status})`;
    throw new BriefblipError(res.status, message);
  }

  return payload as ExtractResult;
}

export function useExtractMeeting() {
  return useMutation({ mutationFn: extractMeeting });
}

export interface TranscribeResult {
  transcript: string;
  engine: string;
  language: string;
  duration_sec: number;
}

// Upload a recording and get its transcript back. Uses XHR (not fetch) so we
// can report upload progress while the file streams to the backend.
export function transcribeAudio(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<TranscribeResult> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('audio', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/api/briefblip/transcribe.php`);
    xhr.responseType = 'json';

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onload = () => {
      const payload = xhr.response as
        | (TranscribeResult & { error?: string })
        | { error?: string }
        | null;
      if (xhr.status >= 200 && xhr.status < 300 && payload && 'transcript' in payload) {
        resolve(payload as TranscribeResult);
      } else {
        const message =
          (payload && 'error' in payload && payload.error) ||
          `Transcription failed (HTTP ${xhr.status})`;
        reject(new BriefblipError(xhr.status, String(message)));
      }
    };
    xhr.onerror = () => reject(new BriefblipError(0, 'Network error during upload.'));
    xhr.send(form);
  });
}
